import { ActionGlyph } from '../../components/ActionGlyph'
import { TourMark } from '../../components/TourMark'
import { useAppActions, useAppState } from '../../state/AppProvider'
import { EmptyState, PageMast, SectionHeading } from '../home/FanPageChrome'
import {
  citySlug,
  formatLongDate,
  showStatusLabel,
  showStatusTone,
  sortedShows,
  type Navigate,
} from '../home/fanExperience'
import '../home/fanExperience.css'

interface ShowDetailPageProps {
  authenticated?: boolean
  navigate: Navigate
  showId: string
}

export function ShowDetailPage({ authenticated = true, navigate, showId }: ShowDetailPageProps) {
  const state = useAppState()
  const actions = useAppActions()
  const shows = sortedShows(state.shows)
  const showIndex = shows.findIndex((item) => item.id === showId)
  const show = shows[showIndex]

  if (!show) {
    return (
      <div className="fan-page">
        <PageMast backTo="/tour" eyebrow="Tour stop" navigate={navigate} title="Stop not found" />
        <EmptyState
          actionLabel="Back to all stops"
          body="This stop is not in your current route."
          onAction={() => navigate('/tour')}
          title="Nothing here"
        />
      </div>
    )
  }

  const previous = shows[showIndex - 1]
  const next = shows[showIndex + 1]
  const saved = authenticated && state.savedShowIds.includes(show.id)
  const ticket = authenticated
    ? state.tickets.find((item) => item.showId === show.id && item.status === 'connected')
    : undefined
  const checkedIn = authenticated && state.checkIns.some((item) => item.showId === show.id)
  const itineraryCount = authenticated ? state.itinerary.filter((item) => item.showId === show.id).length : 0
  const activeShow = authenticated ? state.shows.find((item) => item.id === state.selectedShowId) : undefined
  const showsDifferentCityContext = Boolean(activeShow && activeShow.id !== show.id)

  return (
    <div className="fan-page fan-show-detail">
      <PageMast
        backTo="/tour"
        eyebrow={`Stop ${String(show.sequence).padStart(2, '0')} of ${String(shows.length).padStart(2, '0')}`}
        navigate={navigate}
        title={show.city}
        trailing={(
          authenticated ? (
            <button
              aria-label={`${saved ? 'Remove' : 'Save'} ${show.city}`}
              aria-pressed={saved}
              className="fan-save-button"
              onClick={() => actions.toggleSavedShow(show.id)}
              type="button"
            >
              <TourMark name={saved ? 'check' : 'route'} />
            </button>
          ) : (
            <button className="fan-text-button" onClick={() => navigate('/auth/sign-in')} type="button">Sign in</button>
          )
        )}
      />

      {!authenticated ? (
        <section className="fan-guest-note" aria-label="Guest access">
          <div>
            <p className="fan-eyebrow">Stop preview</p>
            <p>Show details are open to browse. Sign in to save this stop or use personal show tools.</p>
          </div>
          <button className="fan-button fan-button--ink" onClick={() => navigate('/auth/sign-in')} type="button">
            Sign in for show tools
          </button>
        </section>
      ) : null}

      <section className="fan-show-poster" aria-labelledby="show-date-heading">
        <div aria-hidden="true" className="fan-show-poster__flash" />
        <p className="fan-show-poster__date" id="show-date-heading">{formatLongDate(show.isoDate)}</p>
        <h2>{show.venue}</h2>
        <p>{show.doorsLabel}</p>
        <div className={`fan-show-poster__status fan-show-poster__status--${showStatusTone(show)}`}>
          {showStatusLabel(show)}
        </div>
      </section>

      {showsDifferentCityContext && activeShow ? (
        <section className="fan-city-context" aria-labelledby="city-context-heading">
          <div>
            <p className="fan-eyebrow">Home city · {activeShow.city}</p>
            <h2 id="city-context-heading">Viewing {show.city}</h2>
            <p>Community and Home still use {activeShow.city} until you change the active city.</p>
          </div>
          <button
            className="fan-button fan-button--acid fan-city-context__action"
            onClick={() => actions.selectShow(show.id)}
            type="button"
          >
            Make {show.city} active
          </button>
        </section>
      ) : null}

      <section className="fan-show-actions" aria-label="Show actions">
        {!authenticated ? (
          <>
            <button className="fan-action-tile fan-action-tile--acid" onClick={() => navigate('/content')} type="button">
              <span className="fan-action-tile__number">01</span>
              <strong>Tour Desk</strong>
              <small>Read tour guides and updates</small>
              <ActionGlyph />
            </button>
            <button className="fan-action-tile" onClick={() => navigate('/content/content-music-links')} type="button">
              <span className="fan-action-tile__number">02</span>
              <strong>Official links</strong>
              <small>Listen, follow, and shop</small>
              <ActionGlyph />
            </button>
            <button className="fan-action-tile" onClick={() => navigate('/auth/sign-in')} type="button">
              <span className="fan-action-tile__number">03</span>
              <strong>Sign in for show tools</strong>
              <small>Save stops and build your plan</small>
              <ActionGlyph />
            </button>
          </>
        ) : ticket ? (
          <button className="fan-action-tile" onClick={() => navigate('/tickets')} type="button">
            <span className="fan-action-tile__number">01</span>
            <strong>Ticket wallet</strong>
            <small>Section {ticket.section} · Row {ticket.row}</small>
            <ActionGlyph />
          </button>
        ) : (
          <button className="fan-action-tile fan-action-tile--acid" onClick={() => navigate('/tickets/connect')} type="button">
            <span className="fan-action-tile__number">01</span>
            <strong>Connect ticket</strong>
            <small>Add this stop to your wallet</small>
            <ActionGlyph />
          </button>
        )}
        {authenticated ? <button className="fan-action-tile" onClick={() => navigate('/itinerary')} type="button">
          <span className="fan-action-tile__number">02</span>
          <strong>Show-day plan</strong>
          <small>{itineraryCount ? `${itineraryCount} item${itineraryCount === 1 ? '' : 's'} saved` : 'Build your itinerary'}</small>
          <ActionGlyph />
        </button> : null}
        {authenticated ? <button className="fan-action-tile" onClick={() => navigate(`/community/${citySlug(show)}/main`)} type="button">
          <span className="fan-action-tile__number">03</span>
          <strong>City community</strong>
          <small>Main, Updates, Fits, Merch, Scene</small>
          <ActionGlyph />
        </button> : null}
      </section>

      <section className="fan-show-update" aria-labelledby="show-update-heading">
        <SectionHeading eyebrow="Latest city note" id="show-update-heading" title={show.headline} />
        <p>{show.update}</p>
      </section>

      {authenticated && (show.checkInOpen || checkedIn) ? (
        <section className={`fan-checkin-callout ${checkedIn ? 'is-complete' : ''}`}>
          <p className="fan-eyebrow">Show check-in</p>
          <h2>{checkedIn ? 'Your stop is recorded' : ticket ? 'Check-in is open' : 'Connect your ticket first'}</h2>
          <p>{checkedIn ? 'Your passport mark is saved.' : ticket ? 'Use the connected ticket in your wallet.' : 'Ticket access is required for this stop.'}</p>
          <button
            className="fan-button fan-button--ink"
            onClick={() => navigate(ticket || checkedIn ? `/check-in/${show.id}` : '/tickets/connect')}
            type="button"
          >
            {checkedIn ? 'View check-in' : ticket ? 'Check in now' : 'Connect ticket'}
          </button>
        </section>
      ) : null}

      <nav aria-label="Adjacent tour stops" className="fan-adjacent-stops">
        {previous ? (
          <button onClick={() => navigate(`/shows/${previous.id}`)} type="button">
            <small>Previous</small>
            <strong><ActionGlyph direction="back" /> {previous.city}</strong>
          </button>
        ) : <span />}
        {next ? (
          <button onClick={() => navigate(`/shows/${next.id}`)} type="button">
            <small>Next</small>
            <strong>{next.city} <ActionGlyph /></strong>
          </button>
        ) : <span />}
      </nav>
    </div>
  )
}
